/**
 * RosterBoard — the team shift-roster, and the agent's FACE.
 *
 * Renders EVERY teammate as a row — the union of the team's members
 * (GET /teams/{id}/members) and the window's shift rows — so a nurse with zero
 * shifts is still visible, and the invite grid shows the real team. Rows are
 * grouped by qualification (group order = first appearance in the team, then any
 * fold-only qualification, ungrouped last — data-driven, no hardcoded role
 * order). Each row carries its load in the window, and — for the planner — a
 * filled cell can be cleared, which cancels that one shift row.
 *
 * A shift renders as its code on a neutral chip.
 *
 * Responsive:
 *   - md+ : the full nurse × day grid with group headers.
 *   - <md : one day at a time (day pills), nurse rows stacked under group heads.
 *
 * Day columns come from `windowDates` (the horizon); if omitted, columns derive
 * from the entries.
 */

import { Fragment, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type ColumnDef,
  type CellContext,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { cn } from '@/lib/utils'
import { ShiftChip } from '@frontend/components/roster/ShiftChip'
import type { RosterEntry } from '@frontend/components/roster/rosterEntries'
import type { RosterTeamMember } from './useRosterController'
import { ClearShiftMenu } from './ClearShiftMenu'

interface RosterBoardProps {
  entries: RosterEntry[]
  /** Every schedulable teammate → a row, even with zero shifts. */
  team?: RosterTeamMember[]
  /** Horizon day columns. */
  windowDates?: string[]
  /** True while the agent is streaming a roster change — dims + locks the board. */
  isUpdating?: boolean
  /** Planner can clear a filled cell. A viewer gets the read-only board. */
  editable?: boolean
  onClearCell?: (nurseId: string, date: string) => void
}

// One row of the matrix: a teammate and that person's shift per ISO date.
interface RosterRow {
  /** Stable row id (team member id, else the nurse name). */
  id: string
  nurse: string
  qualification: string
  /** date `YYYY-MM-DD` → the shift entry in that cell (one per nurse-day). */
  byDate: Record<string, RosterEntry>
}

interface RosterGroup {
  qualification: string
  rows: RosterRow[]
}

// Locale-aware short column header for an ISO date, e.g. "Mon 23.06".
function formatDayHeader(isoDate: string, locale: string): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(y, (m ?? 1) - 1, d ?? 1, 12)
  if (Number.isNaN(date.getTime())) return isoDate
  return date.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' })
}

export function RosterBoard({
  entries,
  team,
  windowDates,
  isUpdating,
  editable = false,
  onClearCell,
}: RosterBoardProps) {
  const { t, i18n } = useTranslation()

  // Columns: the horizon window when given, else derived from the entries.
  const dates = useMemo(() => {
    if (windowDates && windowDates.length > 0) return windowDates
    const set = new Set<string>()
    for (const e of entries) set.add(e.date)
    return [...set].sort()
  }, [windowDates, entries])

  // Rows = UNION of the ward team and the folded roster. Team seeds the rows
  // (so a zero-shift nurse is visible); entries fold on, reconciled by nurseId
  // when present else by name; an entry whose nurse is off-team still gets a row
  // (never hide an assigned shift).
  const rows = useMemo<RosterRow[]>(() => {
    const byId = new Map<string, RosterRow>()
    const idByName = new Map<string, string>()
    for (const m of team ?? []) {
      const id = m.id || m.name
      if (!byId.has(id)) {
        byId.set(id, { id, nurse: m.name, qualification: m.qualification ?? '', byDate: {} })
      }
      idByName.set(m.name, id)
    }
    for (const e of entries) {
      let rowId: string | undefined
      if (e.nurseId && byId.has(e.nurseId)) rowId = e.nurseId
      else rowId = idByName.get(e.nurse)
      if (!rowId) {
        rowId = e.nurseId || e.nurse
        if (!byId.has(rowId)) byId.set(rowId, { id: rowId, nurse: e.nurse, qualification: '', byDate: {} })
        idByName.set(e.nurse, rowId)
      }
      byId.get(rowId)!.byDate[e.date] = e
    }
    return [...byId.values()]
  }, [team, entries])

  // Group by qualification. Order = first appearance (team first), ungrouped
  // ('') last. No hardcoded role names — the server's team order decides.
  const groups = useMemo<RosterGroup[]>(() => {
    const order: string[] = []
    for (const r of rows) {
      const q = r.qualification
      if (q && !order.includes(q)) order.push(q)
    }
    order.push('')
    const byQual = new Map<string, RosterRow[]>()
    for (const r of rows) {
      const q = r.qualification || ''
      const list = byQual.get(q) ?? []
      list.push(r)
      byQual.set(q, list)
    }
    return order
      .filter((q) => (byQual.get(q)?.length ?? 0) > 0)
      .map((q) => ({
        qualification: q,
        rows: (byQual.get(q) ?? []).sort((a, b) => a.nurse.localeCompare(b.nurse)),
      }))
  }, [rows])

  const orderedRows = useMemo(() => groups.flatMap((g) => g.rows), [groups])
  const showGroupHeaders = groups.length > 1 || (groups.length === 1 && groups[0].qualification !== '')
  const groupLabel = (qualification: string) => qualification || t('roster.group.other', 'Other')

  const countShifts = useCallback(
    (row: RosterRow): number => dates.filter((d) => row.byDate[d]).length,
    [dates],
  )

  // The cell being cleared (the menu is anchored to the tapped cell on desktop).
  const [menu, setMenu] = useState<{ nurseId: string; nurse: string; date: string; code: string; rect: DOMRect | null } | null>(null)
  const closeMenu = useCallback(() => setMenu(null), [])

  // Memoised because the `columns` memo below captures it: an inline function
  // would be a new closure each render, so the columns would either hold a
  // stale one or have to be rebuilt on every render. `editable` is the only
  // value it reads (setMenu is stable), and it is already a columns dep.
  const openMenu = useCallback(
    (row: RosterRow, date: string, el: HTMLElement) => {
      const entry = row.byDate[date]
      if (!editable || !entry) return
      setMenu({ nurseId: row.id, nurse: row.nurse, date, code: entry.shift, rect: el.getBoundingClientRect() })
    },
    [editable],
  )

  const columns = useMemo<ColumnDef<RosterRow>[]>(() => {
    const nurseCol: ColumnDef<RosterRow> = {
      id: 'nurse',
      header: () => t('roster.nurseColumn', 'Nurse'),
      accessorFn: (row) => row.nurse,
      cell: (ctx: CellContext<RosterRow, unknown>) => {
        const row = ctx.row.original
        const load = countShifts(row)
        return (
          <span className="flex items-center gap-2">
            <span className="truncate text-[14px] font-semibold text-[var(--color-surface-ink)]">
              {row.nurse}
            </span>
            <span
              className="shrink-0 rounded-full bg-[var(--color-neutral-100)] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--color-surface-mute)]"
              aria-label={t('roster.load', '{{count}} shifts', { count: load })}
            >
              {load}
            </span>
          </span>
        )
      },
    }
    const dayCols: ColumnDef<RosterRow>[] = dates.map((date) => ({
      id: date,
      header: () => formatDayHeader(date, i18n.language),
      cell: (ctx: CellContext<RosterRow, unknown>) => {
        const row = ctx.row.original
        const entry = row.byDate[date]
        if (!entry) {
          return <span className="text-[13px] text-[var(--color-surface-mute)]" aria-hidden="true">·</span>
        }
        const chip = <ShiftChip code={entry.shift} display={entry.display} size="md" />
        if (!editable) return chip
        return (
          <button
            type="button"
            onClick={(e) => openMenu(row, date, e.currentTarget)}
            aria-label={t('roster.cell.change', 'Change shift for {{nurse}}', { nurse: row.nurse })}
            className="inline-flex min-h-11 items-center justify-center rounded-full transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
          >
            {chip}
          </button>
        )
      },
    }))
    return [nurseCol, ...dayCols]
  }, [dates, t, i18n.language, editable, openMenu, countShifts])

  const table = useReactTable({
    data: orderedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  // Phone single-day view state.
  const [activeDate, setActiveDate] = useState<string | null>(null)
  const phoneDate = activeDate && dates.includes(activeDate) ? activeDate : dates[0] ?? null

  const tableRows = table.getRowModel().rows

  return (
    <div
      className={cn(
        'h-full overflow-auto bg-[var(--color-surface-canvas,#ffffff)]',
        isUpdating && 'pointer-events-none opacity-70',
      )}
    >
      {isUpdating && (
        <div className="sticky top-0 z-10 bg-[var(--color-primary-50)] py-2 text-center text-[14px] font-medium text-[var(--color-primary-700)] motion-safe:animate-pulse">
          {t('roster.optimizing', 'Troi is optimizing…')}
        </div>
      )}

      {/* Phone single-day view (< md). */}
      <div className="md:hidden">
        {phoneDate && (
          <>
            <div className="border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)]">
              <div
                className="flex gap-1 overflow-x-auto p-2"
                role="tablist"
                aria-label={t('roster.daySelector', 'Day')}
              >
                {dates.map((date) => {
                  const selected = date === phoneDate
                  return (
                    <button
                      key={date}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setActiveDate(date)}
                      className={cn(
                        'flex min-h-11 flex-shrink-0 items-center gap-1.5 rounded-full px-4 text-[14px] font-medium transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]',
                        selected
                          ? 'bg-[var(--color-primary-500)] text-[var(--color-neutral-0)]'
                          : 'bg-[var(--color-neutral-100)] text-[var(--color-surface-ink)]',
                      )}
                    >
                      {formatDayHeader(date, i18n.language)}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-4 p-4">
              {groups.map((group) => (
                <div key={group.qualification || '_other'} className="space-y-2">
                  {showGroupHeaders && (
                    <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--color-surface-mute)]">
                      {groupLabel(group.qualification)}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {group.rows.map((row) => {
                      const entry = row.byDate[phoneDate]
                      const load = countShifts(row)
                      const clearable = editable && !!entry
                      return (
                        <li key={row.id}>
                          <button
                            type="button"
                            disabled={!clearable}
                            onClick={(e) => openMenu(row, phoneDate, e.currentTarget)}
                            className={cn(
                              'flex min-h-14 w-full items-center justify-between gap-3 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 text-left transition',
                              clearable && 'active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]',
                            )}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="min-w-0 truncate text-[15px] font-semibold text-[var(--color-surface-ink)]">
                                {row.nurse}
                              </span>
                              <span
                                className="shrink-0 rounded-full bg-[var(--color-neutral-100)] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--color-surface-mute)]"
                                aria-label={t('roster.load', '{{count}} shifts', { count: load })}
                              >
                                {load}
                              </span>
                            </span>
                            {entry ? (
                              <ShiftChip code={entry.shift} display={entry.display} size="md" />
                            ) : (
                              <span className="text-[13px] text-[var(--color-surface-mute)]">
                                {t('roster.offDuty', 'Off')}
                              </span>
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Full grid (md+): nurses as rows, days across. */}
      <div className="hidden overflow-x-auto p-4 md:block">
        <table className="w-full min-w-[640px] border-separate border-spacing-0">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, idx) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cn(
                      'border-b border-[var(--color-surface-border)] px-3 pb-2 text-[13px] font-semibold text-[var(--color-surface-ink)]',
                      idx === 0
                        ? 'sticky left-0 z-10 bg-[var(--color-surface-canvas,#ffffff)] text-left'
                        : 'text-center',
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableRows.map((row, idx) => {
              const qualification = row.original.qualification
              const isGroupStart =
                showGroupHeaders && (idx === 0 || tableRows[idx - 1].original.qualification !== qualification)
              return (
                <Fragment key={row.id}>
                  {isGroupStart && (
                    <tr>
                      <td
                        colSpan={dates.length + 1}
                        className="sticky left-0 border-b border-[var(--color-surface-border)] px-3 pb-1.5 pt-4 text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--color-surface-mute)]"
                      >
                        {groupLabel(qualification)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    {row.getVisibleCells().map((cell, cIdx) => (
                      <td
                        key={cell.id}
                        className={cn(
                          'border-b border-[var(--color-surface-border)] px-3 py-2.5',
                          cIdx === 0
                            ? 'sticky left-0 z-10 max-w-[220px] bg-[var(--color-surface-canvas,#ffffff)] text-left'
                            : 'text-center',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {menu && (
        <ClearShiftMenu
          title={`${menu.nurse} · ${formatDayHeader(menu.date, i18n.language)} · ${menu.code}`}
          anchorRect={menu.rect}
          onClear={() => {
            onClearCell?.(menu.nurseId, menu.date)
            setMenu(null)
          }}
          onClose={closeMenu}
        />
      )}
    </div>
  )
}
